/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, ArrowRight, Mail } from 'lucide-react';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
/* Cache leaders across instances to avoid redundant API calls */
let cachedLeaders = null;
let cachePromise = null;

const fetchLeadersOnce = async () => {
  if (cachedLeaders) return cachedLeaders;
  if (cachePromise) return cachePromise;
  cachePromise = axios.get(`${API}/leadership`).then(res => {
    cachedLeaders = res.data;
    return cachedLeaders;
  }).catch(() => []);
  return cachePromise;
};

/**
 * LeadershipSignature — Premium reusable leadership block
 *
 * Props:
 *   title    - The leadership title to match (e.g. "Board Director of Events")
 *   leaderId - OR pass a specific leader ID
 *   label    - Small label text (default: "Leadership")
 *   blurb    - Optional custom description override
 *   showContact - Show contact button (default: true)
 */
const LeadershipSignature = ({ title, leaderId, label = 'Leadership', blurb, showContact = true }) => {
  const [leader, setLeader] = useState(null);

  useEffect(() => {
    const load = async () => {
      const leaders = await fetchLeadersOnce();
      let match = null;
      if (leaderId) {
        match = leaders.find(l => l.id === leaderId);
      } else if (title) {
        match = leaders.find(l => l.title?.toLowerCase() === title.toLowerCase());
        if (!match) match = leaders.find(l => l.title?.toLowerCase().includes(title.toLowerCase()));
      }
      if (match) setLeader(match);
    };
    load();
  }, [title, leaderId]);

  if (!leader) return null;

  const imgSrc = resolveImg(leader.image_url);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative bg-white rounded-sm overflow-hidden" data-testid={`signature-block-${leader.id}`}>

      {/* Content */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 p-6 sm:p-8">

        {/* Image — top on mobile, right on desktop (we use order to swap) */}
        <div className="sm:order-2 flex-shrink-0">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden shadow-md">
            {imgSrc ? (
              <Img variant="leader" src={imgSrc} alt={leader.name} className="w-full h-full object-cover object-[center_20%]" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#002249] to-[#1D3557] flex items-center justify-center">
                <User className="w-12 h-12 text-white/20" />
              </div>
            )}
          </div>
        </div>

        {/* Text — left on desktop, centered on mobile */}
        <div className="sm:order-1 flex-1 text-center sm:text-left">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#CD1125]/70 mb-2">{label}</p>
          <h3 className="text-xl sm:text-2xl font-bold text-[#002249] tracking-tight mb-0.5">{leader.name}</h3>
          <p className="text-sm font-medium text-[#002249]/60 mb-3">{leader.title}</p>

          {(blurb || leader.bio) && (
            <p className="text-sm text-slate-500 leading-relaxed mb-5 max-w-md">{blurb || leader.bio}</p>
          )}

          <div className="flex items-center gap-3 justify-center sm:justify-start">
            <Link to={`/leadership/${leader.id}`}
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-white bg-[#002249] hover:bg-[#001630] px-5 py-2.5 rounded-sm transition-colors"
              data-testid={`signature-profile-${leader.id}`}>
              View Profile <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            {showContact && leader.email && (
              <a href={`mailto:${leader.email}`}
                className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-[#002249] border border-[#002249]/15 hover:border-[#002249]/30 px-5 py-2.5 rounded-sm transition-colors"
                data-testid={`signature-contact-${leader.id}`}>
                <Mail className="w-3.5 h-3.5" /> Contact
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LeadershipSignature;
